import {
  checkName,
  TNameValidationResponse,
  useCompanyData,
} from '@commenda/api';
import { IncorporationRequestDataType } from '@commenda/types';
import { InfoToolTip, ProceedIcon } from '@commenda/icons';
import {
  Alert,
  Button,
  Checkbox,
  FieldWrapper,
  Input,
  InputErrorMessage,
  Label,
  LoadingIndicator,
  PhoneInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SingleFileUploader,
  Textarea,
  Tooltip,
  useToggle,
} from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import { industryOptions, TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { classNames, fetcher } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CorporationType,
  Country,
  IncorporationRequestStatus,
  LegacyDocumentType,
} from '@prisma/client';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { KeyedMutator } from 'swr';
import RadioCardKS from '../../../../components/RadioCardKS';
import { useForm } from '../../../../hooks/useZodForm';
import { getFileWithType } from '../../../common/helpers';
import {
  CompanyNameInput,
  FormInputWrapper,
  IncorporationFormHeading,
} from '../../common';
import { NameCheckerModal } from '../../common/NameCheckerModal';
import { checkCompanyEmailUniqueness } from '../../../common/helpers';
import { CompanyInfoFormFields, companyInfoSchema } from './schema';

const CompanyInfo = ({
  next,
  data,
  mutate,
  token,
  setNotification,
  companyId,
}: {
  next: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  token: string;
  setNotification: TSetNotification;
  companyId: number;
}) => {
  const router = useRouter();

  const { id } = router.query as { id: string };

  // Get corporations data for cross-entity email validation
  const { corporations } = useCompanyData(companyId);

  const companyNameInputRef = useRef<HTMLDivElement>(null);

  const nameCheckerLoadingToggle = useToggle(false);
  const nameCheckerModalVisibility = useToggle(false);
  const [nameCheckResults, setNameCheckResults] = useState<
    TNameValidationResponse[]
  >([]);

  const industrySelectOptions = industryOptions.map((each) => {
    return {
      value: each,
      text: each,
    };
  });

  const [rentalAgreement, setRenalAgreement] = useState(null);

  const currentRentalAgreement = getFileWithType(
    data?.files || [],
    LegacyDocumentType.RentalAgreement
  );

  const currentCompanyAddressProof = getFileWithType(
    data?.files || [],
    LegacyDocumentType.BillForRegisteredAddressInIndia
  );

  const [billForRegisteredAddress, setBillForRegisteredAddress] = useState(
    currentCompanyAddressProof || null
  );

  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    getValues,
    clearErrors,
  } = useForm<CompanyInfoFormFields>({
    resolver: zodResolver(companyInfoSchema),
    defaultValues: {
      corporationName: data?.requestData?.companyLegalName,
      companyLegalName2: data?.requestData?.companyLegalName2 || '',
      companyLegalName3: data?.requestData?.companyLegalName3 || '',
      usePreReservedName: data?.requestData?.usePreReservedName || false,
      businessDescription: data?.requestData?.businessDescription,
      contactEmail: data?.requestData?.contactEmail,
      contactPhoneNumber: data?.requestData?.contactPhoneNumber || '',
      isOnRentedProperty: data?.requestData?.isOnRentedProperty || false,
      industry: data?.requestData?.industry,
      customIndustry: data?.requestData?.customIndustry,
      companyAddressProof: {
        id: currentCompanyAddressProof?.id || -1,
        documentType: currentCompanyAddressProof?.documentType,
        fileName: currentCompanyAddressProof?.fileName,
        fileUrl: currentCompanyAddressProof?.fileUrl,
      },
      rentalAgreement: {
        id: currentRentalAgreement?.id || -1,
        documentType: currentRentalAgreement?.documentType,
        fileName: currentRentalAgreement?.fileName,
        fileUrl: currentRentalAgreement?.fileUrl,
      },
    },
  });

  const handleIndustryChange = (val: string) => {
    setValue('industry', val);
    if (val !== 'Other') setValue('customIndustry', '');
  };

  const hasNameChanged = () => {
    return (
      data?.requestData?.companyLegalName !== getValues('corporationName') ||
      data?.requestData?.companyLegalName2 !== getValues('companyLegalName2') ||
      data?.requestData?.companyLegalName3 !== getValues('companyLegalName3') ||
      data?.requestData?.usePreReservedName !== getValues('usePreReservedName')
    );
  };

  const saveForm = async (formData: CompanyInfoFormFields) => {
    // Validate cross-entity email uniqueness for Indian corporations
    if (formData.contactEmail && corporations?.length) {
      const emailValidation = checkCompanyEmailUniqueness(
        formData.contactEmail,
        data?.corporationId || -1, // Use -1 for new entities (will never match existing IDs)
        corporations
      );
      
      if (!emailValidation.isUnique) {
        setNotification({
          show: true,
          valid: false,
          message: `This company email is already used by another entity. Each entity must have a unique email address.`,
          title: 'Duplicate Company Email Error',
        });
        return;
      }
    }

    const filesQuery = [];

    filesQuery.push(getValues('companyAddressProof'));
    if (rentalAgreement && watch('isOnRentedProperty'))
      filesQuery.push(getValues('rentalAgreement'));

    const res = await fetcher(
      `workRequest/${id}`,
      {
        method: 'POST',
        body: {
          requestData: {
            companyLegalName: formData.corporationName,
            companyLegalName2: formData.companyLegalName2,
            companyLegalName3: formData.companyLegalName3,
            usePreReservedName: formData.usePreReservedName,
            industry: formData.industry,
            customIndustry: formData.customIndustry,
            businessDescription: formData.businessDescription,
            contactEmail: formData.contactEmail,
            contactPhoneNumber: formData.contactPhoneNumber,
            isOnRentedProperty: formData.isOnRentedProperty,
          },
          files: filesQuery,
          status: IncorporationRequestStatus.InProgress,
        },
      },
      token
    );

    if (!res.ok) {
      setNotification({
        message:
          'Something went wrong, please try again later or contact Team Commenda if you need help',
        show: true,
        valid: false,
        title: 'Error',
      });
      return;
    }

    await mutate();
    next();
  };

  const onSubmit = async (formData: CompanyInfoFormFields) => {
    if (data?.corporationId && !hasNameChanged()) {
      await saveForm(formData);
      return;
    }

    // If using pre-reserved name, skip name validation and duplicate checks
    if (formData.usePreReservedName) {
      await saveForm(formData);
      return;
    }

    if (
      (formData.corporationName &&
        formData.companyLegalName2 &&
        formData.corporationName === formData.companyLegalName2) ||
      (formData.corporationName &&
        formData.companyLegalName3 &&
        formData.corporationName === formData.companyLegalName3) ||
      (formData.companyLegalName2 &&
        formData.companyLegalName3 &&
        formData.companyLegalName2 === formData.companyLegalName3)
    ) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Duplicate company names are not allowed. Please enter a unique company names.',
      });
      return;
    }

    const nameCheckResponses: TNameValidationResponse[] = [];
    nameCheckerLoadingToggle.on();
    nameCheckerModalVisibility.show();

    const nameCheckPromises = [];

    if (formData.corporationName && formData.corporationName.trim()) {
      nameCheckPromises.push(
        checkName({
          name: `${formData.corporationName} LLP`,
          country: Country.IN,
          context: {
            industry: formData.industry as string,
            entityType: CorporationType.LLP,
            companyDescription: formData.businessDescription as string,
          },
        })
      );
    }

    if (formData.companyLegalName2 && formData.companyLegalName2.trim()) {
      nameCheckPromises.push(
        checkName({
          name: `${formData.companyLegalName2} LLP`,
          country: Country.IN,
          context: {
            industry: formData.industry as string,
            entityType: CorporationType.LLP,
            companyDescription: formData.businessDescription as string,
          },
        })
      );
    }

    if (formData.companyLegalName3 && formData.companyLegalName3.trim()) {
      nameCheckPromises.push(
        checkName({
          name: `${formData.companyLegalName3} LLP`,
          country: Country.IN,
          context: {
            industry: formData.industry as string,
            entityType: CorporationType.LLP,
            companyDescription: formData.businessDescription as string,
          },
        })
      );
    }

    const nameCheckResults = await Promise.all(nameCheckPromises);

    nameCheckResults.forEach((result) => {
      if (result.success) {
        nameCheckResponses.push(result.data);
      }
    });

    setNameCheckResults(nameCheckResponses);
    nameCheckerLoadingToggle.off();
  };

  const handleErrorNotifications = (formError: any) => {
    console.error(formError);
    let message = 'Please fix the highlighted fields.';
    if (formError?.companyAddressProof) {
      message = 'Please upload utility bill for registered address.';
    } else if (formError?.rentalAgreement) {
      message = 'Please upload rental agreement.';
    }
    setNotification({
      show: true,
      valid: false,
      title: 'Error',
      message,
    });
  };

  useEffect(() => {
    if (billForRegisteredAddress) {
      setValue('companyAddressProof', {
        ...billForRegisteredAddress,
        id: currentCompanyAddressProof?.id,
      });
    } else {
      setValue('companyAddressProof', null);
    }

    if (rentalAgreement) {
      setValue('rentalAgreement', {
        ...rentalAgreement,
        id: currentRentalAgreement?.id,
      });
    } else {
      setValue('rentalAgreement', null);
    }
  }, [rentalAgreement, billForRegisteredAddress]);

  useEffect(() => {
    if (!data) return;

    const currentBillForRegisteredAddress = getFileWithType(
      data?.files || [],
      LegacyDocumentType.AddressProof
    );

    if (currentBillForRegisteredAddress) {
      setValue('companyAddressProof', {
        id: currentBillForRegisteredAddress.id || -1,
        documentType: currentBillForRegisteredAddress.documentType,
        fileName: currentBillForRegisteredAddress.fileName,
        fileUrl: currentBillForRegisteredAddress.fileUrl,
      });
    }

    if (currentRentalAgreement) {
      const updateRental = {
        id: currentRentalAgreement.id || -1,
        documentType: currentRentalAgreement.documentType,
        fileName: currentRentalAgreement.fileName,
        fileUrl: currentRentalAgreement.fileUrl,
      };
      setValue('rentalAgreement', updateRental);
      setRenalAgreement(updateRental);
    }
  }, [data]);

  return (
    <form
      className="flex flex-col gap-10 max-w-[500px]"
      onSubmit={handleSubmit(onSubmit, handleErrorNotifications)}
    >
      <IncorporationFormHeading
        text="Add Company info"
        additionalText="Please provide information about this company."
      />
      <FormInputWrapper id="industry" text="Industry Type">
        <Select onValueChange={handleIndustryChange}>
          <SelectTrigger
            error={
              !watch('industry') && errors?.industry?.message
                ? errors?.industry?.message
                : null
            }
          >
            <SelectValue placeholder={watch('industry') || 'Select Industry'} />
          </SelectTrigger>
          <SelectContent>
            {industrySelectOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.text}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!watch('industry') && errors?.industry?.message ? (
          <InputErrorMessage
            className="-mt-2"
            message={errors?.industry?.message}
          />
        ) : null}
        {watch('industry') === 'Other' && (
          <Input
            {...register('customIndustry')}
            error={errors.customIndustry?.message}
            placeholder="Enter Industry"
          />
        )}
      </FormInputWrapper>
      <FormInputWrapper
        id="businessDescription"
        text="Business Description"
        description="If your LLP is going to be used to set up a foreign entity, please mention the business description of the foreign entity as that of the LLP"
      >
        <Textarea
          className="py-3 px-[14px] border-border-primary"
          {...register('businessDescription')}
          value={watch('businessDescription')}
          error={errors.businessDescription?.message}
          placeholder="Write a description"
        />
      </FormInputWrapper>

      <FormInputWrapper
        id="usePreReservedName"
        text="Do you want to use a pre-reserved name for your LLP?"
      >
        <div className="flex items-center space-x-2">
          <Checkbox
            id="usePreReservedName"
            checked={watch('usePreReservedName')}
            onCheckedChange={(checked) => {
              setValue('usePreReservedName', checked as boolean);
              if (checked) {
                // Clear the company names when using pre-reserved name
                setValue('corporationName', '');
                setValue('companyLegalName2', '');
                setValue('companyLegalName3', '');
                clearErrors([
                  'corporationName',
                  'companyLegalName2',
                  'companyLegalName3',
                ]);
              }
            }}
          />
          <Label htmlFor="usePreReservedName" className="text-sm font-medium">
            Yes, I want to use a pre-reserved name
          </Label>
        </div>
      </FormInputWrapper>

      {watch('usePreReservedName') ? (
        <Alert
          description="Your incorporation specialist will reach out with some options for pre-reserved names."
          variant="info"
        />
      ) : (
        <FieldWrapper ref={companyNameInputRef} _className="gap-6">
          <div className="flex flex-col gap-2">
            <Label
              htmlFor="PvtLtdName"
              className="text-base font-bold text-neutral-1 relative flex gap-2 items-center"
            >
              Name of the LLP
            </Label>
            <span className="body-15-regular text-neutral-2 leading-[22px]">
              We'll check with the state to see if the name is available.
            </span>
          </div>
          <div>
            <CompanyNameInput
              label="Option 1"
              onValueChange={(val) => {
                setValue('corporationName', val);
                clearErrors('corporationName');
              }}
              error={errors?.corporationName?.message}
              nameValidationOptions={{
                country: Country.IN,
                companyDescription: watch('businessDescription'),
                entityType: CorporationType.LLP,
                industry: watch('industry'),
              }}
              value={watch('corporationName')}
              placeholder="Entity name 1"
            />
          </div>
          <div>
            <CompanyNameInput
              label="Option 2"
              onValueChange={(val) => {
                setValue('companyLegalName2', val);
                clearErrors('companyLegalName2');
              }}
              error={errors?.companyLegalName2?.message}
              nameValidationOptions={{
                country: Country.IN,
                companyDescription: watch('businessDescription'),
                entityType: CorporationType.LLP,
                industry: watch('industry'),
              }}
              value={watch('companyLegalName2')}
              placeholder="Entity name 2"
            />
          </div>
          <div>
            <CompanyNameInput
              label="Option 3"
              onValueChange={(val) => {
                setValue('companyLegalName3', val);
                clearErrors('companyLegalName3');
              }}
              error={errors?.companyLegalName3?.message}
              nameValidationOptions={{
                country: Country.IN,
                companyDescription: watch('businessDescription'),
                entityType: CorporationType.LLP,
                industry: watch('industry'),
              }}
              value={watch('companyLegalName3')}
              placeholder="Entity name 3"
            />
          </div>
        </FieldWrapper>
      )}

      <FormInputWrapper
        id="email"
        text="Company Email"
        toolTip={
          <Tooltip
            side="bottom"
            content="Company email must be unique across all entities and different from director emails."
          >
            <InfoToolTip />
          </Tooltip>
        }
      >
        <Input
          type="email"
          placeholder="Email"
          {...register('contactEmail')}
          error={errors?.contactEmail?.message}
        />
      </FormInputWrapper>

      <FormInputWrapper
        id="phoneNumber"
        text="Phone number"
        description="It should be different from the designated partners."
      >
        <PhoneInput
          id="phoneNumber"
          className="h-[42px]"
          {...register('contactPhoneNumber')}
          onChange={(e) => {
            setValue('contactPhoneNumber', e);
          }}
          value={watch('contactPhoneNumber')}
          error={errors?.contactPhoneNumber?.message}
          onBlur={() =>
            watch('contactPhoneNumber')
              ? clearErrors('contactPhoneNumber')
              : null
          }
        />
      </FormInputWrapper>

      <FormInputWrapper
        text="Proof of address"
        id="billForRegisteredAddress"
        description="Please upload a utility bill not older than 2 months."
        toolTip={
          <Tooltip
            side="right"
            content="Utility bill can be electricity bill, telephone bill, internet bill, mobile bill, rental agreement, gas bill, bank statement."
          >
            <InfoToolTip />
          </Tooltip>
        }
      >
        <SingleFileUploader
          companyId={data?.companyId}
          setFile={setBillForRegisteredAddress}
          file={
            watch('companyAddressProof')?.fileUrl
              ? (watch('companyAddressProof') as FileBasic)
              : null
          }
          error={error}
          setError={setError}
          token={token}
          accept={{
            'application/pdf': ['.pdf'],
            'image/*': ['.png', '.jpg', '.jpeg'],
          }}
          maxSize={10 * 1024 * 1024}
          documentType={LegacyDocumentType.BillForRegisteredAddressInIndia}
          _className="w-full"
        />
      </FormInputWrapper>
      <FormInputWrapper
        id="isOnRentedProperty"
        text="Is this property rented or leased?"
      >
        <RadioCardKS
          heading="Yes"
          onClick={() => setValue('isOnRentedProperty', true)}
          isActive={watch('isOnRentedProperty')}
          iconClassname="size-4"
        />
        <RadioCardKS
          heading="No"
          onClick={() => setValue('isOnRentedProperty', false)}
          isActive={!watch('isOnRentedProperty')}
          iconClassname="size-4"
        />
      </FormInputWrapper>

      {watch('isOnRentedProperty') ? (
        <FormInputWrapper
          text="Rental agreement"
          id="rentalAgreement"
          description="If your LLP is leasing office space, please upload the rental agreement here."
        >
          <SingleFileUploader
            companyId={data?.companyId}
            setFile={setRenalAgreement}
            file={
              watch('rentalAgreement')?.fileUrl
                ? (watch('rentalAgreement') as FileBasic)
                : null
            }
            error={error}
            setError={setError}
            token={token}
            accept={{
              'application/pdf': ['.pdf'],
              'image/*': ['.png', '.jpg', '.jpeg'],
            }}
            maxSize={10 * 1024 * 1024}
            documentType={LegacyDocumentType.RentalAgreement}
            _className="w-full"
          />
        </FormInputWrapper>
      ) : null}

      <Button className="w-fit" disabled={isSubmitting} type="submit">
        {isSubmitting ? <LoadingIndicator size={16} /> : null}
        <span className={classNames(isSubmitting ? 'ml-2' : '')}>Continue</span>
        {!isSubmitting ? <ProceedIcon className="ml-2 h-4 w-4" /> : null}
      </Button>

      <NameCheckerModal
        isOpen={nameCheckerModalVisibility.visible}
        onClose={() => {
          nameCheckerModalVisibility.hide();
        }}
        isLoading={nameCheckerLoadingToggle.value}
        results={nameCheckResults}
        onContinue={handleSubmit(saveForm)}
      />
    </form>
  );
};

export default CompanyInfo;
